/* BG: product page main section behaviour (sections/main-product.liquid). Spec: _specs/20-product-main.md §4, §8.5.
   Works alongside Dawn's product-info.js / product-form.js / media-gallery.js / product-modal.js (all unchanged):
   - <bg-plan-picker>: Subscribe & Save picker (Ella biogold-plan-picker inline script, same behaviour), plus the
     main price switching to the plan price while Subscribe & Save is ticked;
   - <bg-video-modal>: product video pop-up, extends Dawn's ModalDialog (focus trap, Esc, backdrop click);
   - <bg-sticky-atc>: bottom sticky Add to cart bar, drives the main Add to cart button;
   - <bg-product-main>: variant-change sync (plan prices, badges), desktop hover zoom
     (>1024px), looping gallery/thumbnail arrows, prev/next + arrow keys in the lightbox. */

(() => {
  /* ---------- Subscribe & Save picker ---------- */
  if (!customElements.get('bg-plan-picker')) {
    class BgPlanPicker extends HTMLElement {
      connectedCallback() {
        if (this.initialised) return;
        this.initialised = true;
        this.form = this.closest('form');
        if (!this.form) return;
        this.select = this.querySelector('[data-bg-plan-select]');
        this.touched = false;
        this.addEventListener('change', () => {
          this.touched = true;
          this.sync();
        });
        this.sync();

        // If the Recharge widget manages to render, step aside: hide this picker and let Recharge own the
        // selling_plan inputs from then on (watch for 15s, as live).
        if (!this.concede()) {
          const observer = new MutationObserver(() => {
            if (this.concede()) observer.disconnect();
          });
          observer.observe(document.body, { childList: true, subtree: true });
          setTimeout(() => {
            observer.disconnect();
            this.concede();
          }, 15000);
        }
      }

      setPlan(value) {
        let inputs = this.form.querySelectorAll('input[name="selling_plan"]');
        if (!inputs.length) {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = 'selling_plan';
          this.form.appendChild(input);
          inputs = [input];
        }
        inputs.forEach((input) => {
          input.value = value;
        });
      }

      sync() {
        const subscribe = this.querySelector('[data-bg-subscribe] input');
        const subscribed = Boolean(subscribe && subscribe.checked && this.select);
        this.setPlan(subscribed ? this.select.value : '');
        this.showPlanPrice(subscribed);
      }

      // The main price block shows the Subscribe & Save price while that option is ticked
      showPlanPrice(subscribed) {
        const scope = this.closest('bg-product-main') || document;
        scope.querySelectorAll('.bg-product__price-block').forEach((block) => {
          block.classList.toggle('bg-product__price-block--subscribe', subscribed);
        });
      }

      rcRendered() {
        const rc = document.querySelector(
          '.rc-container-wrapper, .rc-container, .rc_container, recharge-widget, .rc-widget, .rc_widget'
        );
        return rc && rc.childElementCount > 0;
      }

      concede() {
        if (this.classList.contains('bg-plan-picker--hidden')) return true;
        if (!this.rcRendered()) return false;
        this.classList.add('bg-plan-picker--hidden');
        if (!this.touched) this.setPlan('');
        this.showPlanPrice(false);
        return true;
      }
    }
    customElements.define('bg-plan-picker', BgPlanPicker);
  }

  /* ---------- Product video modal ---------- */
  const ModalDialogBase = customElements.get('modal-dialog');
  if (ModalDialogBase && !customElements.get('bg-video-modal')) {
    class BgVideoModal extends ModalDialogBase {
      show(opener) {
        // insert the player before super.show() so Dawn's focus trap includes its controls
        const frame = this.querySelector('.bg-video-modal__frame');
        const template = frame && frame.querySelector('template');
        if (template && !frame.querySelector('video, iframe')) {
          const media = template.content.firstElementChild;
          if (media) frame.appendChild(media.cloneNode(true));
        }
        super.show(opener);
        const video = frame && frame.querySelector('video');
        if (video) {
          const playing = video.play();
          if (playing) playing.catch(() => {});
        }
      }

      hide() {
        super.hide();
        // Dawn's hide() pauses every <video>; an external player has to be removed to stop it.
        this.querySelector('.bg-video-modal__frame iframe')?.remove();
      }
    }
    customElements.define('bg-video-modal', BgVideoModal);
  }

  /* ---------- Section controller ---------- */
  /* ---------- Sticky Add to cart bar (snippets/bg-sticky-atc.liquid) ---------- */
  // No form of its own: the button clicks the main Add to cart, so variant, selling_plan and quantity stay in one
  // place. Shown while the buy box is above the viewport and the footer is not yet in view.
  if (!customElements.get('bg-sticky-atc')) {
    class BgStickyAtc extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.section;
        this.source = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
        this.buyBox = this.source && this.source.closest('.bg-product__buy');
        if (!this.buyBox) return;
        this.button = this.querySelector('[data-bg-sticky-button]');
        this.label = this.querySelector('[data-bg-sticky-label]');
        this.spinner = this.button.querySelector('.loading__spinner');
        this.now = this.querySelector('[data-bg-sticky-now]');
        this.was = this.querySelector('[data-bg-sticky-was]');
        this.price = document.getElementById(`price-${this.sectionId}`);
        this.priceBlock = this.price && this.price.closest('.bg-product__price-block');
        this.buyBoxInView = true;
        this.buyBoxReached = false;
        this.footerInView = false;

        this.button.addEventListener('click', () => this.add());

        this.observers = [new MutationObserver(() => this.syncButton())];
        this.observers[0].observe(this.source, {
          attributes: true,
          attributeFilter: ['disabled', 'aria-disabled', 'class'],
          childList: true,
          subtree: true,
          characterData: true,
        });
        if (this.priceBlock) {
          const priceObserver = new MutationObserver(() => this.syncPrice());
          priceObserver.observe(this.priceBlock, {
            attributes: true,
            attributeFilter: ['class'],
            childList: true,
            subtree: true,
            characterData: true,
          });
          this.observers.push(priceObserver);
        }
        this.syncButton();
        this.syncPrice();

        // "Past the buy box" = reached (in view or above it) but not in view. Two observers, because a fast scroll can
        // carry the buy box from below the fold to above it without it ever intersecting the viewport.
        const update = () => this.toggle(this.buyBoxReached && !this.buyBoxInView && !this.footerInView);
        this.inView = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.target === this.buyBox) this.buyBoxInView = entry.isIntersecting;
            else this.footerInView = entry.isIntersecting;
          });
          update();
        });
        this.inView.observe(this.buyBox);
        const footer = document.querySelector('.shopify-section-group-footer-group');
        if (footer) this.inView.observe(footer);
        this.reached = new IntersectionObserver(
          ([entry]) => {
            this.buyBoxReached = entry.isIntersecting;
            update();
          },
          { rootMargin: '100000px 0px 0px 0px' }
        );
        this.reached.observe(this.buyBox);

        // A failed add shows its message in the main form, off-screen: bring the buy box back into view
        this.unsubscribers = [];
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribers.push(
            subscribe(PUB_SUB_EVENTS.cartError, () => {
              if (!this.pending) return;
              this.pending = false;
              const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
              this.buyBox.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'center' });
            }),
            subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
              this.pending = false;
            })
          );
        }
      }

      disconnectedCallback() {
        (this.observers || []).forEach((observer) => observer.disconnect());
        (this.unsubscribers || []).forEach((unsubscribe) => unsubscribe());
        if (this.inView) this.inView.disconnect();
        if (this.reached) this.reached.disconnect();
      }

      add() {
        if (this.source.disabled || this.source.getAttribute('aria-disabled') === 'true') return;
        this.pending = true;
        this.source.click();
      }

      syncButton() {
        const loading = this.source.classList.contains('loading');
        const text = this.source.querySelector('span');
        this.button.disabled = this.source.disabled;
        this.button.setAttribute('aria-disabled', String(this.source.getAttribute('aria-disabled') === 'true'));
        this.button.classList.toggle('loading', loading);
        if (this.spinner) this.spinner.classList.toggle('hidden', !loading);
        if (text && text.textContent.trim()) this.label.textContent = text.textContent.trim();
      }

      // Same price the buy box shows: the Subscribe & Save price while ticked, else the sale or regular price
      syncPrice() {
        if (!this.price) return;
        const read = (selector) => {
          const el = this.price.querySelector(selector);
          return el ? el.textContent.trim() : '';
        };
        const subscribed = this.priceBlock.classList.contains('bg-product__price-block--subscribe');
        const onSale = Boolean(this.price.querySelector('.price--on-sale'));
        let now;
        let was = '';
        if (subscribed && read('.bg-product__price-sub-now')) {
          now = read('.bg-product__price-sub-now');
          was = read('.bg-product__price-sub-was');
        } else if (onSale) {
          now = read('.price__sale .price-item--sale');
          was = read('.price__sale s.price-item--regular');
        } else {
          now = read('.price__regular .price-item--regular');
        }
        if (now) this.now.textContent = now;
        this.was.textContent = was;
        this.was.hidden = !was;
      }

      toggle(show) {
        if (show === this.shown) return;
        this.shown = show;
        this.classList.toggle('bg-sticky-atc--show', show);
        this.inert = !show;
      }
    }
    customElements.define('bg-sticky-atc', BgStickyAtc);
  }

  if (customElements.get('bg-product-main')) return;

  class BgProductMain extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;
      this.sectionId = this.dataset.section;
      this.zoomQuery = window.matchMedia('(min-width: 1025px) and (hover: hover) and (pointer: fine)');

      this.unsubscribers = [];
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribers.push(subscribe(PUB_SUB_EVENTS.variantChange, (event) => this.onVariantChange(event)));
      }

      this.querySelectorAll('slider-component').forEach((slider) => this.loopSlider(slider));
      this.initZoom();
      this.initLightbox();
    }

    disconnectedCallback() {
      (this.unsubscribers || []).forEach((unsubscribe) => unsubscribe());
    }

    /* Dawn re-renders price/options itself; copy the BG pieces it does not know about from the same response */
    onVariantChange({ data }) {
      if (!data || data.sectionId !== this.sectionId || !data.html) return;
      const { html } = data;

      ['onetime', 'subscribe'].forEach((kind) => {
        const source = html.querySelector(`[data-bg-plan-price="${kind}"]`);
        const target = this.querySelector(`[data-bg-plan-price="${kind}"]`);
        if (source && target) target.innerHTML = source.innerHTML;
      });

      const badgesSource = html.getElementById(`BgBadges-${this.sectionId}`);
      const badgesTarget = this.querySelector(`#BgBadges-${this.sectionId}`);
      if (badgesSource && badgesTarget) badgesTarget.innerHTML = badgesSource.innerHTML;
    }

    /* Ella's sliders are infinite: let the arrows wrap instead of disabling at the ends (Dawn's slider is finite) */
    loopSlider(slider) {
      if (!slider.slider || slider.dataset.bgLoop) return;
      slider.dataset.bgLoop = 'true';
      slider.enableSliderLooping = true;
      slider.prevButton?.removeAttribute('disabled');
      slider.nextButton?.removeAttribute('disabled');
      slider.addEventListener(
        'click',
        (event) => {
          const button = event.target.closest('button[name="previous"], button[name="next"]');
          if (!button) return;
          const list = slider.slider;
          const max = list.scrollWidth - list.clientWidth;
          if (max <= 0) return;
          let left = null;
          if (button.name === 'next' && list.scrollLeft >= max - 2) left = 0;
          if (button.name === 'previous' && list.scrollLeft <= 2) left = max;
          if (left === null) return;
          event.preventDefault();
          event.stopImmediatePropagation();
          list.scrollTo({ left });
        },
        true
      );
    }

    /* desktop hover zoom (Ella zoomed-image.js: original file, follows the pointer, fades 120ms; >1024px only) */
    initZoom() {
      const gallery = this.querySelector('media-gallery');
      if (!gallery || !this.querySelector('[data-bg-zoom]')) return;
      gallery.addEventListener('mousemove', (event) => {
        if (!this.zoomQuery.matches) return this.zoomOut();
        const box = event.target.closest('.bg-product__media-box[data-bg-zoom]');
        if (box !== this.zoomBox) {
          this.zoomOut();
          if (box) this.zoomIn(box);
        }
        if (box) this.zoomMove(event);
      });
      gallery.addEventListener('mouseleave', () => this.zoomOut());
    }

    zoomIn(box) {
      this.zoomBox = box;
      let layer = box.querySelector('.bg-product__zoom');
      if (!layer) {
        layer = document.createElement('div');
        layer.className = 'bg-product__zoom';
        layer.setAttribute('aria-hidden', 'true');
        box.appendChild(layer);
      }
      this.zoomLayer = layer;
      const show = () => {
        if (this.zoomBox !== box || !layer.zoomWidth) return;
        if (layer.zoomWidth <= box.clientWidth) return;
        layer.classList.add('is-visible');
        if (this.lastZoomEvent) this.zoomMove(this.lastZoomEvent);
      };
      if (layer.zoomWidth) return show();
      const image = new Image();
      image.onload = () => {
        layer.zoomWidth = image.naturalWidth;
        layer.zoomHeight = image.naturalHeight;
        layer.style.backgroundImage = `url("${box.dataset.bgZoom}")`;
        layer.style.backgroundSize = `${image.naturalWidth}px ${image.naturalHeight}px`;
        show();
      };
      image.src = box.dataset.bgZoom;
    }

    zoomMove(event) {
      this.lastZoomEvent = event;
      const box = this.zoomBox;
      const layer = this.zoomLayer;
      if (!box || !layer || !layer.zoomWidth) return;
      const rect = box.getBoundingClientRect();
      const x = Math.min(Math.max((event.clientX - rect.left) / rect.width, 0), 1);
      const y = Math.min(Math.max((event.clientY - rect.top) / rect.height, 0), 1);
      layer.style.backgroundPosition = `${-x * (layer.zoomWidth - rect.width)}px ${-y * (layer.zoomHeight - rect.height)}px`;
    }

    zoomOut() {
      if (this.zoomLayer) this.zoomLayer.classList.remove('is-visible');
      this.zoomBox = null;
      this.zoomLayer = null;
      this.lastZoomEvent = null;
    }

    /* lightbox: Dawn's product-modal shows one image at a time here; add prev/next buttons and arrow keys */
    initLightbox() {
      const modal = document.getElementById(`ProductModal-${this.sectionId}`);
      if (!modal || modal.dataset.bgNav) return;
      const dialog = modal.querySelector('[role="dialog"]');
      const images = modal.querySelectorAll('.product-media-modal__content > img[data-media-id]');
      const icon = this.querySelector('.slider-button svg');
      if (!dialog || images.length < 2 || !icon) return;
      modal.dataset.bgNav = 'true';

      const step = (delta) => {
        const items = Array.from(modal.querySelectorAll('.product-media-modal__content > img[data-media-id]'));
        let index = items.findIndex((item) => item.classList.contains('active'));
        if (index > -1) items[index].classList.remove('active');
        index = (index + delta + items.length) % items.length;
        items[index].classList.add('active');
      };

      [
        ['prev', -1, this.dataset.labelPrev],
        ['next', 1, this.dataset.labelNext],
      ].forEach(([name, delta, label]) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = `bg-lightbox__nav bg-lightbox__nav--${name}`;
        if (label) button.setAttribute('aria-label', label);
        button.appendChild(icon.cloneNode(true));
        // Dawn's media modal closes on any mouse pointerup; keep the arrows from closing it
        button.addEventListener('pointerup', (event) => event.stopPropagation());
        button.addEventListener('click', (event) => {
          event.stopPropagation();
          step(delta);
        });
        dialog.appendChild(button);
      });

      modal.addEventListener('keydown', (event) => {
        if (!modal.hasAttribute('open')) return;
        if (event.key === 'ArrowRight') step(1);
        if (event.key === 'ArrowLeft') step(-1);
      });
    }
  }

  customElements.define('bg-product-main', BgProductMain);
})();
